import { ArticleItem } from 'types'

const textData: Array<ArticleItem> = [
  {
    text: 'JUEGOS DE IMPROVISACIÓN EN LA MÚSICA CONTEMPORÁNEA:',
    style: ['title1'],
  },

  {
    text: 'Posibilidades de iniciación colectiva al violonchelo',
    style: ['title1'],
  },
  {
    text: 'Autoría: Marta Macedo Brietzke',
    style: ['paragraph', 'margin'],
  },
  {
    text: 'Orientación: Prof. Dr. Fabio Soren Presgrave',
    style: ['paragraph', 'margin'],
  },
  {
    text: 'Co-supervisión: Prof. Dr. Mário André Wanderley Oliveira',
    style: ['paragraph', 'margin'],
  },
  {
    text: '',
    style: ['space'],
  },
  {
    text:
      'Este trabajo forma parte de mi Tesis de Maestría, titulada "La música contemporánea en la iniciación colectiva al violoncelo: una investigación-acción con juegos de improvisación en tres instituciones educativas del estado de São Paulo", defendida el 12 de diciembre de 2018, por la Universidad de São Paulo, con la orientación del Prof. Dr. Fabio Soren Presgrave y la co-supervisión del Prof. Dr. Mário André Wanderley Oliveira.',
    style: ['paragraph', 'italic'],
  },

  {
    text: 'La música contemporánea:',
    style: ['title2', 'anchor'],
  },
  {
    text:
      'El término música contemporánea, utilizado a menudo por intérpretes, profesores y estudiantes de música, carece aún de una definición precisa. Uno de los problemas que conlleva el uso de este término es la idea de que definiría toda y cualquier música producida en la actualidad. Para ayudarnos a definir lo que comúnmente se llama música contemporánea en los círculos académicos, recurrimos a las concepciones de la profesora Bernardete Zagonel. La profesora aclara que, en estos círculos, el término se utiliza con frecuencia para designar la música de concierto europea de los siglos XX y XXI, orientada estéticamente por los profundos cambios filosóficos, científicos, culturales y tecnológicos que se produjeron en este periodo. Cuando nos proponemos escuchar y apreciar la música contemporánea, tenemos que desprendernos de viejos paradigmas y abrir nuestras percepciones para comprender y aprehender otras formas de expresión musical. La música contemporánea puede definirse, pues, como una producción musical vinculada a la música de concierto, ligada a propuestas artísticas formuladas a partir del siglo XX y marcada por la complejidad de diversas referencias artísticas, culturales y filosóficas.',
    style: ['paragraph'],
  },
  {
    text:
      'Aunque estas nuevas características se han consolidado de diferentes maneras desde mediados del siglo XX, aún hoy, a menudo una parte de los músicos profesionales y de los estudiantes de música no están familiarizados e incluso no les gusta tocar o escuchar música que implique posibilidades sonoras diferentes de las consideradas tradicionales (las producidas en siglos anteriores). Sin embargo, también desde el siglo pasado, diferentes músicos, compositores, educadores y profesores de instrumentos han tratado de incorporar este repertorio y estas posibilidades sonoras a las prácticas de los estudiantes. Como ejemplo de estas iniciativas, podemos citar la obra de Béla Bartók y Boris Porena, y también de los brasileños Heitor Villa-Lobos, Ernest Mahle y Bruno Kiefer.',
    style: ['paragraph'],
  },
  {
    text:
      'El uso del repertorio de música contemporánea en las clases de instrumento sigue siendo reducido y poco publicitado. La poca aceptación de este repertorio en las salas de concierto del país puede generar todavía dudas sobre la importancia de su aplicación, especialmente con los estudiantes de nivel inicial. Debemos entender que la aplicación de propuestas vinculadas a la poética de la música contemporánea acerca a los estudiantes a las concepciones y al pensamiento de su propio tiempo, especialmente cuando estas propuestas están vinculadas a actividades de creación, como lo que ocurre en las prácticas de improvisación. Cuando utilizamos el término "poética" para referirnos a la música contemporánea, nos acercamos a una concepción que ve la música no sólo como lenguaje, sino como expresión artística basada en concepciones filosóficas, estéticas, históricas, sociales y políticas de su entorno e incluso en elementos biográficos de los artistas. Este término, utilizado desde la antigüedad por el filósofo griego Aristóteles para referirse a las obras literarias, fue adoptado en la época contemporánea para referirse a otras manifestaciones artísticas, como las artes visuales, las artes escénicas y la música, refiriéndose a todo lo que se produce haciendo uso deliberado de una composición estética.',
    style: ['paragraph'],
  },
  {
    text:
      'A través de propuestas de música contemporánea que implican la improvisación llevamos la creación a la clase de instrumento y acercamos el arte a la vida cotidiana, y el "pensamiento musical", como lo llamó el poeta Augusto de Campos, a la vida diaria de los estudiantes/intérpretes. Actualmente, la "música-pensamiento", o la "música-invención", como se la llama a veces, parece estar destinada a una mínima parte de la población que tiene acceso a la educación musical especializada. De este modo, la apertura a las diferentes manifestaciones musicales, entendidas como fenómenos socioculturales, parece estar condicionada a factores de carácter social y económico, poco democráticos e inclusivos. En Brasil, la educación musical en las escuelas especializadas, con gran frecuencia, se dirige casi exclusivamente a investigar y reproducir repertorios de siglos anteriores al XX y al XXI. Así, el estudiante tiende a realizar sólo obras del pasado. En las principales salas de conciertos brasileñas, el espectáculo musical también parece estar dirigido, casi siempre, a la apreciación de carácter histórico, quedando desfavorecida la idea del arte como agente promotor del pensamiento crítico y la reflexión.',
    style: ['paragraph'],
  },
  {
    text:
      'Nos corresponde a los profesores de música, junto con los compositores e investigadores del área, destacar el valor de la música como producto y productor de experiencias y pensamiento. Para ello, la interpretación de obras musicales actuales, desde el inicio de la formación del estudiantes/intérprete es fundamental para poder llevar el "pensamiento musical" a nuestra vida cotidiana.',
    style: ['paragraph'],
  },
  {
    text: 'La improvisación:',
    style: ['title2', 'anchor'],
  },
  {
    text: `La improvisación es una práctica que se ha desarrollado a lo largo de los siglos y de diferentes maneras en muchas culturas, como la india, la árabe y la persa, entre otras. En las culturas europeas, esta práctica fue habitual hasta mediados del siglo XIX, cuando los papeles de compositor e intérprete comenzaron a delimitarse como autoexcluyentes. La práctica de la improvisación siguió siendo abordada, generalmente en la música popular, en estilos como el jazz, el choro, la salsa, en manifestaciones artísticas conocidas como "improvisación libre", así como en piezas de compositores poéticos contemporáneos que a veces destinan pasajes a la improvisación de los intérpretes.`,
    style: ['paragraph'],
  },
  {
    text:
      ' Desde mediados del siglo XX, la práctica de la improvisación ha sido también una importante estrategia pedagógica para la enseñanza de la música. Sin embargo, esta práctica se aplica poco en las clases de instrumentos musicales, según la investigadora Chefa Alonso, a excepción de los países nórdicos. Comprender la importancia de las actividades vinculadas a la improvisación y algunas propuestas para utilizar esta práctica nos ayuda, como profesores, a desarrollar conceptos en los que esta práctica se incluye en las actividades planificadas para nuestras clases. Los investigadores y educadores musicales destacan en sus trabajos e informes que, a través de las actividades relacionadas con la improvisación, es posible presentar contenidos musicales a los alumnos a la vez que la música se convierte en un vehículo para la expresión de percepciones, pensamientos y sentimientos.',
    style: ['paragraph'],
  },
  {
    text:
      'A partir de los trabajos del investigador Derek Bailey, que datan del año 1993, la improvisación comenzó a ser clasificada según algunas características relacionadas con su idiomaticidad. Aunque el uso de estos términos ha sido relativizado por el investigador que los definió, siguen siendo ampliamente utilizados. Se llama "improvisación idiomática" a la que está vinculada a un estilo o "lenguaje" musical concreto y "improvisación no idiomática" a la que no está relacionada con un lenguaje concreto, sino con el sonido, con sus posibles relaciones y transformaciones. Entre las prácticas relacionadas como "no idiomáticas" se encuentra también la "improvisación libre", que incluye una serie de manifestaciones que se definen como tales. En común, estas prácticas desarrollan un enfoque en el rendimiento, que incluye, en mayor o menor medida, una actuación activa y creativa en tiempo real. Los educadores musicales, en la mayoría de los casos, abordan en sus procesos de enseñanza y aprendizaje algunos elementos de propuestas vinculadas a la improvisación libre.',
    style: ['paragraph'],
  },
  {
    text:
      'La práctica de la improvisación libre como objeto artístico apareció en Alemania e Inglaterra en los años sesenta del siglo XX, a través de la creación de diversos grupos dedicados a esta práctica. Paralelamente, los educadores musicales empezaron a observar que los niños ya improvisan libremente de forma natural y que, si se estimula esta práctica, se pueden incorporar elementos musicales a sus experiencias y aprendizajes. Los educadores destacan algunos de los beneficios de la improvisación, entre ellos el aprendizaje de nuevas formas de relación interpersonal basadas en la igualdad, la profundización de la relación con el instrumento, una mayor comprensión de las composiciones musicales, la necesidad de resolver problemas que se presentan como similares a los de la vida cotidiana, el desarrollo de la creatividad, la autoexpresión, la autoestima y la autonomía.',
    style: ['paragraph'],
  },
  {
    text: 'Los juegos en el aprendizaje:',
    style: ['title2', 'anchor'],
  },
  {
    text:
      'Educadores y pensadores de diversas áreas han ido desarrollando a lo largo del tiempo, ideas sobre el aprendizaje a través de los juegos, sin embargo, los investigadores actuales consideran que estas ideas aún no están claras para todas las personas que trabajan directamente con la educación. Una de las razones de esta confusión es la característica polisémica de la palabra juego, que puede utilizarse para referirse a actividades con diferentes significados. El juego ha sido estudiado como una parte natural y esencial del ser humano, una parte esencial del aprendizaje y de la transmisión del conocimiento y de la propia cultura.',
    style: ['paragraph'],
  },
  {
    text:
      'Para los investigadores que se ocupan del aprendizaje a través de los juegos, éstos no tienen un "objetivo educativo" determinado, sino que consideran que todo lo que aprendemos, lo hacemos a través de los juegos. Los juegos de improvisación propuestos en este trabajo siguen esta idea, no tienen como objetivo el desarrollo de contenidos específicos, sino que están destinados a trabajar la naturaleza lúdica innata de los niños y adolescentes.',
    style: ['paragraph'],
  },
  {
    text: 'La enseñanza colectiva del violoncelo:',
    style: ['title2', 'anchor'],
  },
  {
    text:
      'En nuestra práctica como profesores, a menudo encontramos instituciones en las que la enseñanza tiene lugar sólo de forma individual o tutorial, y otras en las que la enseñanza se produce de forma colectiva. En Brasil, la enseñanza colectiva suele tener lugar en instituciones u ONGs donde el flujo de estudiantes es constante y es necesario atender a un gran número de estudiantes al mismo tiempo, mientras que en las escuelas libres o los conservatorios, la enseñanza se dirige a una sola persona a la vez. Sin embargo, la opción por la enseñanza colectiva va más allá de las restricciones económicas, teniendo en cuenta sus objetivos y concepciones.',
    style: ['paragraph'],
  },
  {
    text:
      'Se estima que la enseñanza de instrumentos en grupo, tal como la llamamos hoy, comenzó en el siglo XIX en Inglaterra y posteriormente en Estados Unidos. En Brasil, comenzó a adoptarse en los años cincuenta, y en los setenta surgió un gran movimiento para la enseñanza colectiva de las cuerdas frotadas, encabezado por Alberto Jaffé y Dayse de Luca. A partir de la difusión de la enseñanza colectiva del instrumento, los investigadores iniciaron la investigación sobre los beneficios de dicho modelo de enseñanza, beneficios que, además de musicales, son de orden psicológico y social.',
    style: ['paragraph'],
  },
  {
    text:
      'En la enseñanza colectiva, se cree que es posible compartir el conocimiento, el espacio, y que la interacción y la diferencia son partes importantes del aprendizaje. Además de estos valores, la práctica de la música en conjunto proporciona una mayor diversificación de los contenidos musicales tratados, ya que incluye algunas variables que son imposibles en la enseñanza individual. Hay que tener en cuenta que la enseñanza colectiva no debe ser una copia de la individual, sólo que dirigida a más personas al mismo tiempo. Una de las características de la enseñanza colectiva es que todos los estudiantes deben estar activos en todo momento durante la clase. Por lo tanto, la actuación del profesor debe ser también diferenciada, capaz de reunir a todos los alumnos en función de los objetivos propuestos. Las prácticas de los juegos de improvisación que aquí se ofrecen sólo son posibles en este contexto, ya que están dirigidas al aprendizaje colectivo del instrumento en asociación con los objetivos y las concepciones de este proceso.',
    style: ['paragraph'],
  },
  {
    text: 'Actividades prácticas:',
    style: ['title2', 'anchor'],
  },
  {
    text:
      'El pedagogo, investigador y compositor François Delalande, que forma parte del grupo francés GRM, fundado por Pierre Schaeffer en 1951, a través de una intensa investigación con niños, señala que, comúnmente, al comenzar a estudiar un instrumento musical, los alumnos tienen una postura similar a la del compositor de música concreta. Señala, por ejemplo, la necesidad exploratoria con fines expresivos de los cuerpos sonoros, en nuestro caso, el chelo. Como profesores, podemos observar esta necesidad en la tendencia natural del alumno a percutir, raspar, frotar y producir diferentes acciones con diversos resultados sonoros. También podemos observar que los niños y adolescentes, en la fase inicial de estudio del violonchelo, no tienen prejuicios estéticos como los que presentan los estudiantes o músicos más avanzados técnicamente. Los alumnos principiantes aceptan el violonchelo como una posibilidad infinita de producir música diferente, con sus multiplicidades estéticas. Esta observación nos lleva a concluir que las dificultades de comprensión de la música contemporánea por parte de los músicos profesionales es una dificultad planteada por la repetición sistemática de ciertos estilos musicales, generalmente los pertenecientes a la música de concierto europea de los siglos XVIII y XIX.',
    style: ['paragraph'],
  },
  {
    text:
      'Por lo tanto, se puede concluir que una forma de empezar a enseñar el repertorio de música contemporánea y los juegos de improvisación es permitir que el estudiante ejercite su curiosidad natural en la exploración de los sonidos del entorno, entre ellos, los sonidos que puede producir el violonchelo. Para ello, podemos utilizar algunas ideas de algunos compositores de música contemporánea, como la narratividad, el paisaje sonoro y la mímesis. La mímesis es un recurso utilizado por diferentes lenguajes artísticos, entre ellos, la música. En música, la mímesis consiste básicamente en la imitación de fenómenos ambientales o psicológicos a través de las cualidades del sonido y la construcción de narraciones musicales.',
    style: ['paragraph'],
  },
  {
    text:
      'Durante la exploración del violonchelo, la mímesis también puede utilizarse como recurso pedagógico. Los diferentes sonidos producidos por el violonchelo pueden relacionarse, junto con los alumnos, con contenidos psicológicos y ambientales. Este enfoque permite a los alumnos comprender que los sonidos producidos a través de las técnicas extendidas, es decir, las formas no convencionales de abordar el instrumento, no son sólo ruido, sino una parte importante de la gama expresiva musical. Así, los alumnos comienzan a producir contenidos musicales cada vez más subjetivos, abarcando tanto las técnicas convencionales como las extendidas. De este modo, son capaces de expresar sus pensamientos, sentimientos y emociones a través de ideas musicales.',
    style: ['paragraph'],
  },
  {
    text: '',
    style: ['line'],
  },
  {
    text: 'Juegos de improvisación colectiva:',
    style: ['title1'],
  },
  {
    text: 'Descripción del juego 1',
    style: ['paragraph', 'bold', 'anchor'],
  },
  {
    text: 'Pregunta y respuesta com cuerdas sueltas en pizzicato',
    style: ['title2'],
  },
  {
    text: 'Autoría: Marta Brietzke',
    style: ['paragraph', 'italic'],
  },
  {
    text: 'Objetivos:',
    style: ['paragraph', 'bold'],
  },
  {
    text:
      'Proporcionar a los estudiantes una conciencia de su individualidad y de su papel dentro del colectivo; establecer las ideas de narrativa musical, preguntas y respuestas, haciendo hincapié en el uso de las cuatro cuerdas del violonchelo, el pizzicato, los diferentes ritmos, la métrica, los timbres y la dinámica.',
    style: ['paragraph'],
  },
  {
    text: 'Metodología y aplicación:',
    style: ['paragraph', 'bold'],
  },
  {
    text:
      'Comenzamos el juego explicando a los estudiantes lo que podría ser una pregunta y una respuesta dentro de una "narración" musical. Podemos dar un ejemplo con nuestro propio instrumento, utilizando sólo las cuerdas sueltas. En este punto, una buena estrategia para que los estudiantes comprendan los conceptos de pregunta y respuesta es que utilicemos variaciones de dinámica o ritmo.',
    style: ['paragraph'],
  },
  {
    text:
      'A continuación, proponemos que cada uno de los estudiantes elabore una respuesta musical a una pregunta que le haremos. El profesor hace una pregunta y el primer estudiante responde. A continuación, el profesor hace otra pregunta y el segundo estudiante responde, y así sucesivamente hasta que todos los estudiantes hayan participado en el juego. Podemos hacer tantas rondas como creamos necesarias, dependiendo de cada clase o grupo de estudiantes. Las preguntas pueden abarcar diferentes dinámicas, ritmos y métricas, de modo que los estudiantes se vean estimulados de diferentes maneras.',
    style: ['paragraph'],
  },
  {
    text:
      'A continuación, pedimos a los estudiantes que se dividan en parejas y elijan quién hará la pregunta y quién dará la respuesta. Cada pareja se coloca en una parte diferente de la sala y es importante que los estudiantes tengan experiencia en ambos papeles. La idea es que, tras este momento, los estudiantes puedan "presentar", por parejas, a los demás compañeros una serie de preguntas y respuestas que han preparado.',
    style: ['paragraph'],
  },
  {
    text:
      'Es importante que permitamos a los estudiantes cierta autonomía. Es interesante que dejemos que sean ellos los que definan cuántas preguntas y respuestas quieren presentar a sus compañeros, quién empieza a preguntar, qué señal acordarán para el inicio y el final de las sesiones de preguntas y respuestas. A menudo observaremos un aumento de la complejidad de las preguntas y respuestas formuladas por los estudiantes en comparación con las preparadas al principio del juego.',
    style: ['paragraph'],
  },
  {
    text: 'Fuente: elaborado por la autora.',
    style: ['paragraph', 'citation'],
  },
  {
    text: '',
    style: ['line'],
  },
  {
    text: 'Descripción del Juego 2',
    style: ['paragraph', 'bold', 'anchor'],
  },
  {
    text: 'LUZ VERDE X LUZ ROJA',
    style: ['title2'],
  },
  {
    text: 'Autoría: Maria Teresa Alencar de Brito',
    style: ['paragraph', 'italic'],
  },
  {
    text: 'Adaptación: Marta Brietzke',
    style: ['paragraph', 'italic'],
  },
  {
    text: 'Objetivos:',
    style: ['paragraph', 'bold'],
  },
  {
    text:
      'Trabajar las diferencias entre el sonido y el silencio, así como su importancia y papel en la construcción de la música; profundizar en la importancia de la colectividad dentro del proceso musical; establecer la idea de diálogo musical.',
    style: ['paragraph'],
  },
  {
    text: 'Metodología y aplicación:',
    style: ['paragraph', 'bold'],
  },
  {
    text:
      'Antes de su realización, podemos proponer a los estudiantes que exploren las diferentes sonoridades de sus violoncelos. Una forma de realizar esta práctica es a través de la imitación: proponemos un sonido y pedimos a los estudiantes que repitan, juntos o individualmente, ese mismo sonido. Es interesante que podamos nombrar las técnicas que ya tienen un nombre definido por la literatura, por ejemplo, con legno, sul ponticelo, pizzicato Bartók. Es importante que dejemos espacio para que los estudiantes descubran sus propios sonidos, destinando una parte de esta exploración para que se acerquen a sus instrumentos por sí mismos. Podemos pedir a cada uno que demuestre a los demás compañeros un sonido que haya descubierto, para, de nuevo, dar voz al estudiante, y luego todos repiten ese sonido.',
    style: ['paragraph'],
  },
  {
    text:
      'También podemos elegir algunos de estos sonidos y compartirlos con los estudiantes, si consideramos que el aprendizaje de todos los sonidos el mismo día puede provocar alguna dificultad de asimilación. Más adelante, en las próximas lecciones, podemos introducir algunos de estos sonidos más, con el fin de aumentar el rango expresivo de los estudiantes. En algunos grupos de estudiantes, que aún no saben sostener el arco, podemos ofrecer estos mismos sonidos, proponiendo que los alumnos sostengan el arco "abrazándolo" en la cuenta. De este modo, los estudiantes estarán cómodos y no adquirirán vicios posturales. La mayoría de los sonidos pueden producirse con la "sujeción" del arco de esta manera, lo que permite desarrollar los juegos en paralelo a la adquisición técnica de los estudiantes.',
    style: ['paragraph'],
  },
  {
    text:
      'Después de explorar el instrumento, mostramos a los estudiantes dos pequeños carteles, uno verde y otro rojo. Asociamos estos carteles con las normas de tráfico, en las que el verde significa seguir o si no tocar, y el rojo significa parar o no tocar. Podemos proponer que los alumnos elijan cada uno uno de los sonidos inventariados anteriormente, o que produzcan diferentes sonidos. Cuando se presenta la señal verde, los alumnos deben tocar un sonido en su violonchelo. Cuando se presenta la señal roja, los alumnos deben hacer una pausa. De este modo, establecemos la importancia y la función del silencio desde una perspectiva musical.',
    style: ['paragraph'],
  },
  {
    text:
      'Es interesante dar a los estudiantes la experiencia de ser el "director de orquesta", es decir, la persona que elegirá cuándo hacer ruido, cuándo callar. Después de unas cuantas rondas del juego, podemos distribuir una luz verde más y una luz roja más, estableciendo dos "conductores" y dividiendo el grupo en dos. Cada grupo debe seguir las directrices de su director específico, lo que dará lugar a diálogos musicales entre los dos grupos y a un sonido más complejo.',
    style: ['paragraph'],
  },
  {
    text:
      'Los directores deben comunicarse entre sí para definir el principio y el final de la música, y a nosotros nos corresponde establecer que esta comunicación debe ser no verbal. Hay que destacar en esta fase del juego que es importante que tanto los directores como los intérpretes escuchen los diferentes sonidos producidos por el grupo y tomen conciencia de la "narrativa" musical que se está creando. Hay que animar a los directores a crear, junto con las señales verdes y rojas, señales para las variaciones de dinámica, articulación y carácter. Por ejemplo, bajar la señal puede indicar que se toca más el piano, y subir la señal puede significar que se toca más fuerte. Debemos estar atentos y estimular que el poder de escucha de los alumnos se profundice con cada ronda del juego, evitando que se vuelva mecánico y repetitivo.',
    style: ['paragraph'],
  },
  {
    text:
      'También es interesante en este momento mostrar la grabación de alguna pieza musical que utilice técnicas extendidas, como la pieza Pression de Helmut Lachenmann, disponible en varias versiones en la web de youtube. Esta audición acerca el trabajo de los alumnos al de otras personas, lo que es importante para que los alumnos valoren su propia producción y la de los demás. ¡Otra pieza interesante son las improvisaciones por contraseñas interpretadas por el grupo de violoncelistas argentinos ARRE! Algunas de estas improvisaciones también están disponibles en la web de youtube y permiten al alumno escuchar diferentes sonidos, producidos por un grupo de violonchelistas y, como en el caso de Lachenmann, acercan el trabajo realizado por los alumnos con lo que producen los músicos en la actualidad.',
    style: ['paragraph'],
  },
  {
    text: 'Fuente: Elaboración propia a partir de las propuestas de Maria Teresa Alencar de Brito.',
    style: ['paragraph', 'citation'],
  },
  {
    text: '',
    style: ['line'],
  },
  {
    text: 'Descripción del Juego 3',
    style: ['paragraph', 'bold', 'anchor'],
  },
  {
    text: 'La noche en el castillo',
    style: ['title2'],
  },
  {
    text: 'Autoría: Claudia Freixedas',
    style: ['paragraph', 'italic'],
  },
  {
    text: 'Objetivos:',
    style: ['paragraph', 'bold'],
  },
  {
    text:
      'Establecer una relación entre lo imaginario y lo sonoro; reforzar la confianza entre compañeros; desarrollar el poder de elección dentro de la creación musical.',
    style: ['paragraph'],
  },
  {
    text: 'Metodología y aplicación:',
    style: ['paragraph', 'bold'],
  },
  {
    text:
      'En este juego contamos una historia imaginando un castillo encantado en medio de una noche de tormenta. Animamos a los alumnos a narrar qué elementos estarían presentes en este castillo, como telas de araña, murciélagos, fantasmas, así como elementos de la naturaleza, como truenos, ráfagas de viento, lluvia. En esta tormenta, el viajero se acercará al castillo y entrará en contacto con estas diferentes manifestaciones.',
    style: ['paragraph'],
  },
  {
    text:
      'Distribuimos a los estudiantes en diferentes partes de la sala, preferiblemente con cierta distancia entre ellos. Cada estudiante elegirá el sonido que quiera tocar o se puede establecer que los alumnos puedan tocar más de un tipo de sonido, en función de lo que consideren adecuado para el contexto musical. Cada estudiante representará un objeto del castillo, no siendo necesario nombrar cada uno de estos objetos y dejándolo a la imaginación de cada uno, dando posibilidades a las asociaciones subjetivas que hagan los alumnos.',
    style: ['paragraph'],
  },
  {
    text:
      'Un estudiante elegido por el grupo será el viajero. Este estudiante tendrá los ojos vendados con un pañuelo, simulando la oscuridad del castillo. El objetivo es que el viajero atraviese el castillo sin chocar con ningún objeto. Para ello, cuando el viajero se acerque a un objeto, el alumno responsable del mismo deberá producir el sonido elegido. Cuando el viajero se aleja del objeto, el sonido disminuye en intensidad y se reproduce el sonido del objeto más cercano al viajero.',
    style: ['paragraph'],
  },
  {
    text:
      'A medida que jugamos las rondas de este juego, podemos añadir complejidades. Podemos sugerir al viajero que se quede más tiempo frente a un objeto cuyo sonido quiere escuchar o que vuelva a un sonido que ya ha escuchado y quiere volver a oír. De este modo, el estudiante viajero se convierte en una especie de director de orquesta y sus elecciones producen una determinada "narrativa" musical.',
    style: ['paragraph'],
  },
  {
    text:
      'Es interesante hacer una grabación de audio o vídeo de la improvisación y luego escuchar esta grabación juntos y discutir el resultado. La escucha de la pieza completa permite a todos los participantes hacerse una idea general de la propuesta musical elaborada. A veces, los estudiantes que participan en la actuación pueden perder la noción del conjunto musical y esta audición es fundamental para que los alumnos comprendan que no están produciendo sólo ruidos inconexos, sino un "camino" musical.',
    style: ['paragraph'],
  },
  {
    text: 'Fuente: Elaboración propia a partir de las propuestas de Claudia Freixedas.',
    style: ['paragraph', 'citation'],
  },
  {
    text: '',
    style: ['line'],
  },
  {
    text: 'Descripción del Juego 4',
    style: ['paragraph', 'bold', 'anchor'],
  },
  {
    text: 'Firma Musical',
    style: ['title2'],
  },
  {
    text: 'Autoría: Wanderson Cruz',
    style: ['paragraph', 'italic'],
  },
  {
    text: 'Objetivos:',
    style: ['paragraph', 'bold'],
  },
  {
    text:
      'Permitir la experiencia de la creación a partir de células musicales preestablecidas; proporcionar diferencias entre polifonía y homofonía; fomentar el poder de elección dentro de la creación musical.',
    style: ['paragraph'],
  },
  {
    text: 'Metodología y aplicación:',
    style: ['paragraph', 'bold'],
  },
  {
    text:
      'Empezamos preguntando a los estudiantes qué es una firma. Poco después, relacionamos esta respuesta con lo que sería una "firma musical". Podemos dar un ejemplo con nuestro propio instrumento, siendo interesante que utilicemos sonidos convencionales y sonidos de técnicas extendidas.',
    style: ['paragraph'],
  },
  {
    text:
      'A continuación, proponemos que cada estudiante cree su propia firma musical utilizando los sonidos que desee. Damos un tiempo para que los estudiantes hagan esta investigación y elijan individualmente su firma. Después, proponemos que cada estudiante realice su firma para el resto del grupo, para que todos puedan conocer todas las firmas. Es interesante pedir a los estudiantes que ejecuten su firma más de una vez, para poder comprobar si mantienen siempre la misma firma o si cada vez ejecutan una firma diferente. En este juego, proponemos que las firmas de cada estudiante sean siempre las mismas.',
    style: ['paragraph'],
  },
  {
    text:
      'Después de que los estudiantes hayan actuado, proponemos componer una pieza utilizando las firmas musicales de cada uno de ellos. Elegimos un director de orquesta para organizar la composición. El director realiza señales con sus manos y su cuerpo indicando cuándo debe tocar cada estudiante su firma, con qué dinámica y cuándo debe dejar de tocar. La idea es que, hasta que el director de orquesta dé la señal de parar, las firmas se reproduzcan continuamente. Cuando el estudiante termina su firma, vuelve a empezar desde el principio hasta que recibe la señal de parar.',
    style: ['paragraph'],
  },
  {
    text:
      'Hay que estimular a los estudiantes para que trabajen su escucha y su elección artística frente al material presentado por sus compañeros. De este modo, volvemos a evitar que el juego se vuelva mecánico y facilitamos la construcción de una idea musical coherente.',
    style: ['paragraph'],
  },
  {
    text:
      'Es interesante, en todos los juegos, que el profesor sea el director de orquesta la primera vez que se juega, pero que, después, se elija a diferentes alumnos para que asuman este papel. De este modo, hacemos posible que todos los estudiantes del grupo puedan, en algún momento, participar como directores de uno de los juegos de improvisación.',
    style: ['paragraph'],
  },
  {
    text: 'Fuente: Elaborado por la autora a partir de las propuestas de Wanderson Cruz.',
    style: ['paragraph', 'citation'],
  },
  {
    text: '',
    style: ['line'],
  },
  {
    text: 'Descripción del Juego 5',
    style: ['paragraph', 'bold', 'anchor'],
  },
  {
    text: 'Igual X Parecido X Diferente',
    style: ['title2'],
  },
  {
    text: 'Autoría: Violeta Gainza',
    style: ['paragraph', 'italic'],
  },

  {
    text: 'Adaptación: Marta Brietzke',
    style: ['paragraph', 'italic'],
  },

  {
    text: 'Objetivos:',
    style: ['paragraph', 'bold'],
  },
  {
    text:
      'Trabajar las ideas de similitud y diferencia en la música; aportar la creación a partir de un elemento previamente dado; trabajar la idea de frases o narraciones musicales.',
    style: ['paragraph'],
  },
  {
    text: 'Metodología y aplicación:',
    style: ['paragraph', 'bold'],
  },
  {
    text:
      'Presentamos a los estudiantes tres pequeños carteles. En el primer póster presentamos el signo matemático utilizado para igual, en el segundo el signo matemático utilizado para diferente y en el tercer póster podemos crear algún signo que represente lo similar. Proponemos a los estudiantes que respeten las señales que se les ofrecerán. El primer alumno crea libremente una frase musical. A continuación, el profesor presenta uno de los signos al segundo estudiante, que debe repetir o variar la frase según el signo representado. A continuación, el profesor ofrece un nuevo signo al tercer alumno, que ahora debe repetir o variar la frase presentada por el segundo. El juego continúa hasta que todos los estudiantes hayan creado sus frases. Los signos pueden ser elegidos al azar o previamente seleccionados por el profesor.',
    style: ['paragraph'],
  },
  {
    text:
      'A continuación, proponemos que uno de los estudiantes sea el director de orquesta y dé las indicaciones de las señales a sus compañeros. Podemos añadir complejidad al juego proponiendo que el director de orquesta, además de elegir el siguiente cartel que ofrecerá a los compañeros, elija también, siempre de forma no verbal, al estudiante que ejecutará la siguiente variación de la frase.',
    style: ['paragraph'],
  },
  {
    text: 'Fuente: Elaboración propia a partir de las propuestas de Violeta Gainza.',
    style: ['paragraph', 'citation'],
  },
  {
    text: '',
    style: ['line'],
  },
  {
    text: 'Descripción del Juego 6',
    style: ['paragraph', 'bold', 'anchor'],
  },
  {
    text: 'Composición con imágenes y escritura com notación propia',
    style: ['title2'],
  },
  {
    text: 'Autoría: Diversos autores',
    style: ['paragraph', 'italic'],
  },

  {
    text: 'Adaptación: Marta Brietzke',
    style: ['paragraph', 'italic'],
  },

  {
    text: 'Objetivos:',
    style: ['paragraph', 'bold'],
  },
  {
    text:
      'Acercar a los estudiantes a la idea de composición; desarrollar una simbología propia que represente sus ideas musicales; tomar contacto con diferentes formas de escritura y representación musical; llamar la atención sobre las limitaciones de cualquier tipo de escritura musical; acercar a compositores e intérpretes.',
    style: ['paragraph'],
  },
  {
    text: 'Metodología y aplicación:',
    style: ['paragraph', 'bold'],
  },
  {
    text:
      'Para el juego de composición con imágenes pedimos en la lección anterior que los estudiantes trajeran imágenes de revistas o fotos personales. Sin embargo, es interesante que el profesor traiga algunas imágenes previamente seleccionadas, porque a veces los estudiantes se olvidan de traerlas y la actividad se ve comprometida. El profesor también debe traer carteles de cartón y juegos de lápices de colores. También podemos añadir otros materiales, según nuestra disponibilidad, como palillos, cintas de colores, cuerdas de diferentes grosores, piedras, etc.',
    style: ['paragraph'],
  },
  {
    text:
      'Comenzamos el juego dividiendo la clase en grupos. Cada grupo presentará sus dibujos y fotos a los demás compañeros. El profesor puede distribuir más imágenes a los estudiantes. A continuación, proponemos a los estudiantes que elijan una o varias imágenes o fotos, tantas como deseen, para contar una historia musical. Podemos poner un ejemplo con nuestro propio violoncelo eligiendo una de las imágenes. Hay que insistir en que la historia debe tener una trama, un principio, un medio y un final, y que los estudiantes deben discutir y elaborar esta trama juntos, de acuerdo con la(s) imagen(es) que elijan.',
    style: ['paragraph'],
  },
  {
    text:
      'Tras la elaboración de la trama, animamos a los estudiantes a producir contenidos musicales con sus violonchelos que representen la historia creada por ellos. La historia puede contarse por partes, o incluso simultáneamente, en función de las elecciones de los estudiantes. La selección de estos contenidos debe hacerse en conjunto por los grupos y podemos darles todo el tiempo que creamos necesario para que investiguen y seleccionen los sonidos deseados.',
    style: ['paragraph'],
  },
  {
    text:
      'En un tercer momento del juego, distribuimos los carteles, lápices de colores y otros materiales y pedimos a los estudiantes que creen una forma de "escribir" la música que han inventado. Esta escritura puede utilizar dibujos, colores, símbolos, palabras, dependiendo de lo que cada grupo encuentre conveniente. Podemos sugerir que elijan un título para su pieza, que pongan sus nombres como compositores, o podemos ni siquiera mencionar tales aspectos, dando a cada grupo la libertad de determinar lo que considere necesario para entender la pieza.',
    style: ['paragraph'],
  },
  {
    text:
      'Cuando los estudiantes terminan de escribir, les pedimos que ensayen juntos la pieza que han creado, utilizando la partitura o no. Debemos darles autonomía para que establezcan entre ellos las señales de inicio y fin de la música, las señales dinámicas y otras informaciones que consideren necesarias. Después de algunos ensayos, proponemos un momento de presentación de las piezas a los compañeros del otro grupo. Podemos sugerir que los estudiantes cuenten su historia y luego interpreten la pieza, o que interpreten la pieza y luego cuenten su historia, o simplemente mostrar las imágenes elegidas por ellos para la creación de sus piezas, dependiendo de lo que consideremos interesante en ese momento. Es importante que en este momento también demos autonomía a los estudiantes en su organización y presentación a los demás compañeros.',
    style: ['paragraph'],
  },
  {
    text: 'Fuente: Elaboración propia.',
    style: ['paragraph', 'citation'],
  },
  {
    text: '',
    style: ['line'],
  },
  {
    text: 'Descripción del Juego 7',
    style: ['paragraph', 'bold', 'anchor'],
  },
  {
    text: 'Improvisación con ostinato',
    style: ['title2'],
  },
  {
    text: 'Autoría: Varios autores',
    style: ['paragraph', 'italic'],
  },
  {
    text: 'Adaptación: Marta Brietzke',
    style: ['paragraph', 'italic'],
  },
  {
    text: 'Objetivos:',
    style: ['paragraph', 'bold'],
  },
  {
    text:
      'Trabajar la noción de solo y tutti; trabajar la idea de ostinato; trabajar el concepto de forma musical.',
    style: ['paragraph'],
  },
  {
    text: 'Metodología y aplicación:',
    style: ['paragraph', 'bold'],
  },
  {
    text:
      'Empezamos este juego de improvisación explicando a los estudiantes qué es un ostinato. Poco después, se propone algún ostinato rítmico para ser ejecutado por todos los estudiantes con la cuerda en pizzicato. Este ostinato debe ser sencillo, como por ejemplo, una negra seguida de dos corcheas.',
    style: ['paragraph'],
  },
  {
    text:
      'A continuación, proponemos a los estudiantes que el grupo se divida en orquesta y solista. El grupo que representará a la orquesta ejecutará el ritmo de ostinato con un solo acorde D, mientras que el estudiante elegido para ser el solista ejecutará diferentes frases, englobando los conocimientos musicales y técnicos ya aprendidos por ellos. No es necesario, en este momento, pensar en la armonía o en la conducción vocal, porque la improvisación no necesita obedecer a los patrones estéticos del tonalismo. El objetivo es que los estudiantes puedan utilizar libremente sus conocimientos musicales y organizar sus ideas, teniendo como punto de partida un ritmo de ostinato.',
    style: ['paragraph'],
  },
  {
    text:
      'Podemos organizar la improvisación de diferentes maneras, como por ejemplo estableciendo que tocaremos el ritmo de ostinato dos veces después del final de cada solo, para que el siguiente intérprete pueda identificar su momento para entrar como solista en la actuación. También podemos organizar la improvisación en forma de rondó, proponiendo que la parte A sea un número determinado de veces de repetición de ostinato o algún tema creado en conjunto por el grupo, para ser tocado por el solista antes de su improvisación libre, etc. Organizamos y añadimos complejidad al juego en el transcurso de las jugadas, que pueden intercalarse con sesiones de escucha colectiva de las grabaciones de las improvisaciones.',
    style: ['paragraph'],
  },
  {
    text: 'Fuente: Elaboración propia.',
    style: ['paragraph', 'citation'],
  },
  {
    text: '',
    style: ['line'],
  },
  {
    text: 'Descripción del Juego 8',
    style: ['paragraph', 'bold', 'anchor'],
  },
  {
    text: 'El bosque encantado',
    style: ['title2'],
  },
  {
    text: 'Autoría: Claudia Freixedas',
    style: ['paragraph', 'italic'],
  },
  {
    text: 'Objetivos:',
    style: ['paragraph', 'bold'],
  },
  {
    text: 'Trabajar el diálogo musical; trabajar la idea de polifonía.',
    style: ['paragraph'],
  },
  {
    text: 'Metodología y aplicación:',
    style: ['paragraph', 'bold'],
  },
  {
    text:
      'Consiste en una variación del juego La noche en el castillo, con una complejidad añadida. En este caso, también creamos una historia en la que dos exploradores llegan a un bosque encantado. En este bosque, cada árbol emite un sonido diferente. Cuando los exploradores se acercan a los árboles, hacen su propio sonido. Podemos jugar a este juego con los ojos abiertos o con los ojos vendados, de forma similar al juego La noche en el castillo, y los árboles pueden emitir un único sonido o diferentes sonidos, según el contexto musical. Es interesante, sin embargo, proponer algunas diferencias entre este juego y el anterior.',
    style: ['paragraph'],
  },
  {
    text:
      'En este juego, una de las complejidades que se presentan a los alumnos es que más de un explorador entra en el bosque al mismo tiempo. Esta característica permitirá la existencia de un diálogo que será "compuesto" por los exploradores, que también actuarán como directores de orquesta.',
    style: ['paragraph'],
  },
  {
    text: 'Fuente: Elaboración propia a partir de las propuestas de Claudia Freixedas.',
    style: ['paragraph', 'citation'],
  },
  {
    text: '',
    style: ['line'],
  },
  {
    text: 'Descripción del Juego 9',
    style: ['paragraph', 'bold', 'anchor'],
  },
  {
    text: 'Paseo en el zoológico',
    style: ['title2'],
  },
  {
    text: 'Autoría: Violeta Gainza',
    style: ['paragraph', 'italic'],
  },
  {
    text: 'Objetivos:',
    style: ['paragraph', 'bold'],
  },
  {
    text:
      'Trabajar diferentes posibilidades musicales de una misma sonoridad; trabajar la idea de narrativa musical.',
    style: ['paragraph'],
  },
  {
    text: 'Metodología y aplicación:',
    style: ['paragraph', 'bold'],
  },
  {
    text:
      'En este juego, también creamos, junto con los alumnos, una historia sobre un visitante en un zoológico. El visitante recorre el zoo y observa los diferentes animales. Cada alumno elegirá un animal para representarlo con su violonchelo. El visitante elegirá qué animal quiere visitar poniéndose delante de cada uno de los compañeros que le representarán.',
    style: ['paragraph'],
  },
  {
    text:
      'Así, como ya se ha dicho, podemos grabar las improvisaciones resultantes, aumentando el poder de escucha y análisis de los alumnos, haciendo posible que en las siguientes rondas de los juegos las interacciones musicales provengan cada vez más de la elección artística de los estudiantes. Hay que evitar siempre que la ejecución de los juegos se convierta en una actividad mecánica y desprovista de análisis crítico, porque, en este caso, se pierde una importante estrategia de desarrollo musical de los estudiantes.',
    style: ['paragraph'],
  },
  {
    text: 'Fuente: Elaboración propia a partir de las propuestas de Violeta Gainza.',
    style: ['paragraph', 'citation'],
  },
  {
    text: '',
    style: ['line'],
  },
  {
    text: 'Descripción del Juego 10',
    style: ['paragraph', 'bold', 'anchor'],
  },
  {
    text: 'Los pájaros y el cazador',
    style: ['title2'],
  },
  {
    text: 'Autoría: Claudia Freixedas',
    style: ['paragraph', 'italic'],
  },
  {
    text: 'Adaptación: Marta Brietzke',
    style: ['paragraph', 'italic'],
  },
  {
    text: 'Objetivos:',
    style: ['paragraph', 'bold'],
  },
  {
    text:
      'Trabajar sobre la libertad y las diferentes posibilidades de inicio, desarrollo y fin de la "pieza" musical; trabajar sobre cuestiones relacionadas con la ecología y la ecología acústica; trabajar sobre los diálogos, las polifonías y la noción de forma musical.',
    style: ['paragraph'],
  },
  {
    text: 'Metodología y aplicación:',
    style: ['paragraph', 'bold'],
  },
  {
    text:
      'En este juego preguntamos a los estudiantes qué sonidos de pájaros conocen. Podemos hacer colectivamente un inventario de estos sonidos o proponernos investigar en nuestras casas y llevar los resultados de nuestras investigaciones al resto de los compañeros. También podemos llevar un catálogo de sonidos de aves grabados que podemos obtener de sitios de Internet. De este modo, elegimos algunos pájaros que queremos imitar y buscamos sonidos similares en nuestros violoncelos.',
    style: ['paragraph'],
  },
  {
    text:
      'Tras elegir los sonidos de las aves, cada alumno se encarga de emitir uno de estos sonidos, representando una especie de aves. A continuación, elegimos juntos un sonido representativo que imite el sonido producido por el cazador. Este sonido puede ser el de un rifle disparando, el del cazador caminando por el bosque o el que determine la imaginación de los alumnos.',
    style: ['paragraph'],
  },
  {
    text:
      'Proponemos, pues, que en el bosque donde se encuentran los pájaros, cada uno de ellos actúa de forma que dialoga con los demás pájaros, creando conscientemente un contenido sonoro. Un estudiante se encargará de hacer el sonido del cazador. Todos los pájaros, al oír el sonido del cazador, se callan por un momento. Luego, cuando ya no hay peligro, los pájaros vuelven a cantar poco a poco, estableciendo de nuevo un diálogo con los demás pájaros del bosque.',
    style: ['paragraph'],
  },
  {
    text:
      'El cazador puede intervenir en el bosque tantas veces y con la frecuencia que desee. También hay que dar autonomía para que el propio grupo sienta el tiempo necesario de silencio tras la intervención del cazador, el momento de iniciar la improvisación y el momento de terminarla. De este modo, ampliamos la percepción del grupo en relación con la actuación y la participación de cada uno en el conjunto musical.',
    style: ['paragraph'],
  },
  {
    text: 'Fuente: Elaboración propia a partir de las propuestas de Claudia Freixedas.',
    style: ['paragraph', 'citation'],
  },
  {
    text: '',
    style: ['line'],
  },
  {
    text: 'Sugerencia de repertorio:',
    style: ['title1', 'anchor'],
  },
  {
    text:
      'Para contextualizar el trabajo con juegos de improvisación dentro de la poética de la música contemporánea y ofrecer subsidios a los estudiantes, podemos presentar, en diferentes momentos, piezas del repertorio de los siglos XX y XXI, compuestas para violonchelo. El objetivo de la escucha de estas piezas es que los estudiantes puedan identificar elementos comunes de estas piezas con sus propias creaciones y así dar más valor a su trabajo y al de los compositores presentados.',
    style: ['paragraph'],
  },
  // TODO: Verificar no arquivo traduzido se esta lista não foi traduzida para o espanhol.
  {
    text: 'Lista de piezas del repertorio contemporáneo para violoncelo',
    style: ['paragraph', 'bold'],
  },
  {
    text: 'Lachenmann, Helmut - Presión, para violonchelo solo',
    style: ['itemList'],
  },
  {
    text: 'ARRE! - Improvisaciones con señas para ensemble',
    style: ['itemList'],
  },
  {
    text: 'Penderecki, Kristof - Capriccio per Siegfried Palm, para violonchelo solo',
    style: ['itemList'],
  },
  {
    text: 'Berio, Luciano - Sequenza XIV, para violonchelo solo',
    style: ['itemList'],
  },
  {
    text: 'Boulez, Piérre - Messagesquisse, para violonchelo solo y seis violonchelos',
    style: ['itemList'],
  },
  {
    text: 'Saraaiaho, Kaija - Sept Papillons, para violonchelo solo',
    style: ['itemList'],
  },
  {
    text: 'Ligeti, Györgi - Sonata, para violonchelo solo',
    style: ['itemList'],
  },
  {
    text: 'Romitelli, Fausto - Professor Bad Trip- lesson II- cadenza para violonchelo',
    style: ['itemList'],
  },
  {
    text: 'Furrer, Beat - Solo für cello, para violonchelo solo',
    style: ['itemList'],
  },
  {
    text: 'Scelsi, Giancinto - Trilogia, para violonchelo solo',
    style: ['itemList'],
  },
  {
    text: 'Crumb, George - Sonata para Violonchelo, para violonchelo solo',
    style: ['itemList'],
  },
  {
    text: 'Kagel, Maurício - Match, para dos violonchelos e percusión',
    style: ['itemList'],
  },
  {
    text: 'Sciarrino, Salvatore - Variazoni, para violonchelo e orquesta',
    style: ['itemList'],
  },
  {
    text: 'Saunders, Rebecca - Solitude, para violonchelo solo',
    style: ['itemList'],
  },
  {
    text: 'Rihm, Wolfgang - Von Weit, para violonchelo e piano',
    style: ['itemList'],
  },
  {
    text: 'Ginastera, Alberto - Puneña, para violonchelo solo',
    style: ['itemList'],
  },
  {
    text: 'Gras, Germán - Solo para Marta, para violonchelo solo',
    style: ['itemList'],
  },
  {
    text: 'Kiefer, Bruno - Errância, para violonchelo solo',
    style: ['itemList'],
  },
  {
    text: 'Santoro, Claúdio - Fantasia Sulamérica, para violonchelo solo',
    style: ['itemList'],
  },
  {
    text: 'Nobre, Marlos - Desafio, para violonchelo e orquesta',
    style: ['itemList'],
  },
  {
    text: 'Lima, Rodrigo - Circuncello, para violonchelo e orquesta',
    style: ['itemList'],
  },
  {
    text: 'Ferraz, Sílvio - Lamento quase mudo, para violonchelo solo',
    style: ['itemList'],
  },
  {
    text: 'Angelo, Bruno - Traçado íntimo e hesitante, para violonchelo solo',
    style: ['itemList'],
  },
  {
    text: 'Fuente: Elaborado por la autora.',
    style: ['paragraph', 'citation'],
  },
  {
    text: '',
    style: ['line'],
  },
  {
    text: 'Para saber más:',
    style: ['title2', 'anchor'],
  },
  {
    text: 'Lista de libros y publicaciones sobre los temas tratados',
    style: ['paragraph', 'bold'],
  },
  {
    text:
      'ALONSO, Chefa. Improvisación libre: la composición en movimiento. Baiona: Editorial Dos acordes S. L., 2007.',
    style: ['itemList'],
  },
  {
    text:
      'DELALANDE, François. La música es un juego de niños. Buenos Aires: Ricordi Americana, 2001.',
    style: ['itemList'],
  },
  {
    text:
      'COSTA, Rogério. Música errante: o jogo da improvisação livre. São Paulo: Perspectiva: Fapesp, 2016.',
    style: ['itemList'],
  },
  {
    text:
      'GAINZA, Violeta. La improvisación musical. Buenos Aires: Melos de Ricordi Americana, 2009.',
    style: ['itemList'],
  },
  {
    text:
      'KISHIMOTO, Tizuko Morchida. O jogo e a educação infantil. São Paulo: Cengage Learning, 2013.',
    style: ['itemList'],
  },
  {
    text:
      'MATTHEWS, Wade. Improvisando: La libre creación musical. Madrid: Turner Publicaciones S. L., 2012.',
    style: ['itemList'],
  },
  {
    text:
      'TOURINHO, Cristina, TOURINHO, Cristina. Ensino coletivo de instrumentos musicais: crenças, mitos, princípios e um pouco de história. In: XVI Encontro Nacional da ABEM, 2007. Mato Grosso do Sul.',
    style: ['itemList'],
  },
  {
    text: 'Fuente: elaborado por la autora.',
    style: ['paragraph', 'citation'],
  },
  {
    text: '',
    style: ['line'],
  },
  {
    text: 'Referencia:',
    style: ['title1'],
  },
  {
    text: 'BRIETZKE, Marta Macedo. ',
    titleStrong:
      'La música contemporánea en la iniciación colectiva al violonchelo: una investigación-acción con juegos de improvisación en tres instituciones educativas del estado de São Paulo. ',
    rest:
      '2018. 159 f. Dissertação (Mestrado) - Escola de Comunicações e Artes - USP, São Paulo, 2018.',
    style: ['bibliography'],
  },
]

export default textData
